import React from 'react';
import { CommonModalProps } from 'components/CommonModal/context';
import InstantCheckoutSlotExpiredErrorModalContent from 'components/InstantCheckoutModalContents/InstantCheckoutSlotExpiredErrorModalContent';
import { ModalSeverityValues } from 'constants/modals/modalSeverityValues';
import { urls } from 'constants/urls';
import { PlaceOrder409Error } from 'services/orderOrchestration/types';

const reloadPage = () => {
  window.location.reload();
};
const getGoToCheckout = (section?: 'checkoutPromoCodes') => () => {
  window.location.href = `${urls.checkout}${section ? `#${section}` : ''}`;
};
const goToTrolleyPage = () => {
  window.location.href = urls.trolleyPage;
};
const goToServiceSelection = () => {
  window.location.href = urls.serviceSelection;
};
const goToMyDetailsPage = () => {
  window.location.href = urls.myDetailsPage;
};

const defaultInstantCheckoutErrorModal: CommonModalProps = {
  id: 'instant-checkout-default-error-modal',
  messageText: ['Please try again'],
  severity: ModalSeverityValues.ERROR,
  titleText: "Sorry, there's been a problem",
  handleModalClose: reloadPage,
  buttons: [
    {
      buttonText: 'Try again',
      theme: 'primary',
      onClick: reloadPage,
    },
  ],
};

const instantCheckoutPreviewResultsChangedModal: CommonModalProps = {
  id: 'instant-checkout-preview-results-have-changed',
  titleText: 'Instant Checkout is unavailable',
  messageText: [
    'Updates have been made to your order. Please continue to checkout as normal to ensure you’re happy with the changes.',
  ],
  handleModalClose: getGoToCheckout(),
  buttons: [
    {
      buttonText: 'Proceed to checkout',
      theme: 'primary',
      onClick: getGoToCheckout(),
    },
  ],
};

const instantCheckoutGiftCardOrVoucherErrorModal: CommonModalProps = {
  id: 'instant-checkout-gift-card-or-voucher-was-added-error-modal',
  titleText: 'Sorry, there is an issue with Instant Checkout',
  messageText: ['Please checkout as normal without using Instant Checkout'],
  handleModalClose: event => {
    if (event.label !== 'Cancel') {
      getGoToCheckout()();
    }
  },
  buttons: [
    {
      buttonText: 'Proceed to checkout',
      theme: 'primary',
      onClick: () => {
        window.location.href = urls.checkout;
      },
    },
    {
      buttonText: 'Cancel',
      theme: 'secondary',
    },
  ],
};

export const getInstantCheckoutErrorModal = (
  error: PlaceOrder409Error,
  location: string = 'unknown',
): CommonModalProps => {
  const getInstantCheckoutErrorModalsByCode: Record<string, CommonModalProps> = {
    ORDER_ORCHESTRATION_017: {
      id: 'instant-checkout-no-slot-booked',
      titleText: 'You still need to book a slot',
      messageText: ['To checkout, you must book a slot.'],
      cannotClose: true,
      handleModalClose: goToServiceSelection,
      buttons: [
        {
          buttonText: 'Book a slot',
          theme: 'primary',
          onClick: goToServiceSelection,
        },
      ],
    },
    ORDER_ORCHESTRATION_029: {
      id: 'instant-checkout-order-without-slot-error-modal',
      titleText: "Sorry, you've been timed out",
      messageText: [
        "You'll need to re-book your slot before you can check out. Your shopping is still in your trolley.",
      ],
      handleModalClose: goToServiceSelection,
      buttons: [
        {
          buttonText: 'OK',
          theme: 'primary',
          onClick: goToServiceSelection,
        },
      ],
    },
    ORDER_ORCHESTRATION_041: {
      id: 'instant-checkout-trolley-hard-conflict-check-error-modal',
      titleText: 'There are problems with your trolley',
      messageText: ['Please address the issues displayed in your trolley before you continue'],
      handleModalClose: goToTrolleyPage,
      buttons: [
        {
          buttonText: 'View trolley',
          theme: 'primary',
          onClick: goToTrolleyPage,
        },
      ],
    },
    ORDER_ORCHESTRATION_042: {
      id: 'instant-checkout-trolley-minimum-spend-not-met-error-modal',
      titleText: 'Minimum spend not met',
      messageText: ['To checkout with this order, please meet the minimum spend requirement'],
      handleModalClose: goToTrolleyPage,
      buttons: [
        {
          buttonText: 'OK',
          theme: 'primary',
          onClick: goToTrolleyPage,
        },
      ],
    },
    ORDER_ORCHESTRATION_043: {
      id: 'instant-checkout-trolley-minimum-spend-not-met-error-modal',
      titleText: 'Add a grocery item to checkout',
      messageText: [
        'You have booked a mixed grocery and Entertaining slot. Please add a grocery item to checkout with this order. Alternatively, cancel this order and book an Entertaining-only slot',
      ],
      handleModalClose: goToTrolleyPage,
      buttons: [
        {
          buttonText: 'OK',
          theme: 'primary',
          onClick: goToTrolleyPage,
        },
      ],
    },
    ORDER_ORCHESTRATION_044: {
      id: 'instant-checkout-order-confirmation-conflict',
      cannotClose: true,
      messageText: ['These issues are usually temporary. Please try again.'],
      titleText: 'Sorry, there was a problem confirming your slot',
      handleModalClose: goToServiceSelection,
      buttons: [
        {
          buttonText: 'Book a slot',
          theme: 'primary',
          onClick: goToServiceSelection,
        },
      ],
    },
    ORDER_ORCHESTRATION_058: {
      id: 'checkout-instant-checkout-slot-expired-error-modal',
      titleText: 'Sorry, the new slot you requested is unavailable',
      severity: ModalSeverityValues.CONFLICT,
      children: (
        <InstantCheckoutSlotExpiredErrorModalContent
          location={location}
          getInstantCheckoutErrorModal={getInstantCheckoutErrorModal}
        />
      ),
    },
    ORDER_ORCHESTRATION_091: {
      id: 'instant-checkout-promo-code-invalid-error-modal',
      titleText: 'Unable to apply promo voucher/code',
      messageText: [
        'Your promo voucher and/or code does not fulfil its full terms of use. To place your order, please remove them or meet the promo terms of use.',
      ],
      handleModalClose: getGoToCheckout('checkoutPromoCodes'),
      buttons: [
        {
          buttonText: 'Cancel',
          theme: 'secondary',
          onClick: goToTrolleyPage,
        },
        {
          buttonText: 'Review then checkout',
          theme: 'primary',
          onClick: getGoToCheckout('checkoutPromoCodes'),
        },
      ],
    },
    ORDER_ORCHESTRATION_111: {
      id: 'instant-checkout-delivery-date-unavailable-error-modal',
      titleText: 'Sorry, there was a problem',
      messageText: [
        'An issue with your order is preventing you from checking out. Please call Customer Services on 0800 188 884',
      ],
      handleModalClose: goToTrolleyPage,
      buttons: [
        {
          buttonText: 'OK',
          theme: 'primary',
          onClick: goToTrolleyPage,
        },
      ],
    },
    ORDER_ORCHESTRATION_123: instantCheckoutGiftCardOrVoucherErrorModal,
    ORDER_ORCHESTRATION_124: instantCheckoutGiftCardOrVoucherErrorModal,
    ORDER_ORCHESTRATION_125: {
      id: 'instant-checkout-error-entertaining-collection-no-address',
      messageText: [
        'Please provide an address to complete your order. You will then be able to checkout',
      ],
      titleText: 'Add address',
      handleModalClose: goToMyDetailsPage,
      buttons: [
        {
          buttonText: 'Add address',
          theme: 'primary',
          onClick: goToMyDetailsPage,
        },
      ],
    },
    ORDER_ORCHESTRATION_127: {
      id: 'instant-checkout-tra-exemption-not-valid-error-modal',
      titleText: 'Sorry, please use standard checkout',
      messageText: ['Following updates you have made to this order, standard checkout is required'],
      handleModalClose: getGoToCheckout(),
      buttons: [
        {
          buttonText: 'Checkout',
          theme: 'primary',
          onClick: getGoToCheckout(),
        },
      ],
    },
    ORDER_ORCHESTRATION_136: instantCheckoutPreviewResultsChangedModal,
    ORDER_ORCHESTRATION_137: instantCheckoutPreviewResultsChangedModal,
    TIMEOUT: {
      ...defaultInstantCheckoutErrorModal,
      id: 'checkout-timeout-error',
      webviewId: 'checkout-timeout-error',
    },
  };

  const instantCheckoutErrorModal =
    getInstantCheckoutErrorModalsByCode[error.code] || defaultInstantCheckoutErrorModal;
  return instantCheckoutErrorModal;
};
